import * as React from "react";

import { cn } from "@/lib/utils";

interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** SVG illustration (preferred). */
  hero?: React.ReactNode;
  /** Lucide icon (fallback nếu không có hero). */
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Subtle help line dưới CTA. */
  hint?: React.ReactNode;
  /** Padding compact hoặc default. */
  size?: "sm" | "md" | "lg";
}

/**
 * EmptyState — universal empty placeholder với illustration + CTA.
 *
 * Doc 28 §6 + Doc 31. Replace tất cả "Chưa có ..." plain text.
 */
export function EmptyState({
  hero,
  icon,
  title,
  description,
  action,
  hint,
  size = "md",
  className,
  ...rest
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center mx-auto",
        size === "sm" && "py-8 max-w-sm",
        size === "md" && "py-12 max-w-md",
        size === "lg" && "py-16 max-w-lg",
        className,
      )}
      {...rest}
    >
      {hero ? (
        <div className="mb-4 text-primary/60" aria-hidden>
          {hero}
        </div>
      ) : icon ? (
        <div className="mb-4 size-16 rounded-2xl bg-gradient-brand-soft flex items-center justify-center text-primary" aria-hidden>
          {icon}
        </div>
      ) : null}
      <h3 className="text-base font-semibold mb-1">{title}</h3>
      {description ? (
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
      ) : null}
      {action ? <div className="mb-3">{action}</div> : null}
      {hint ? (
        <div className="text-[11px] text-muted-foreground/80 max-w-xs">
          {hint}
        </div>
      ) : null}
    </div>
  );
}
